<template>
  <v-card :outlined="outlined"
          :class="`mb-3 rounded-card ${outlined ? '': 'card-shadow'}`">
    <v-card-text class="text--primary">
      <v-list-item dense>
        <v-list-item-content>
          <px-produto-descricao
            :especificacao="compraProduto.especificacaoProdutoOrigem
              || compraProduto.especificacaoProduto"
            :produto="compraProduto.produto"/>&nbsp;
          <div class="caption">
            <px-gestao-entrega-produto-situacao
              show-suffix-icon
              :situacao="compraProduto.situacaoEntrega"/>
          </div>
        </v-list-item-content>
        <v-list-item-content>
          {{
            compraProduto.saldoEntregue
              | number(getCurrencyFormat(compraProduto.produto.casasDecimais))
          }}/{{
            compraProduto.quantidade
              | number(getCurrencyFormat(compraProduto.produto.casasDecimais))
          }}&nbsp;
          {{ compraProduto.unidadeMedida.descricao }}
          <div class="caption">
            Saldo
          </div>
        </v-list-item-content>
        <v-list-item-content v-if="compraProduto.compra">
          {{ compraProduto.compra.fornecedor.pessoa.nome | truncate(30) }}
        </v-list-item-content>
        <v-list-item-content>
          <span>
          {{ dataPrevisaoEntregaGeral | date }}
            <span v-if="isAtrasado" class="error--text">
              <v-icon right class="pb-1 error--text">mdi-circle-medium</v-icon>
              Atrasado
            </span>
            <span v-if="isHoje" class="text--primary--color">
              <v-icon right class="pb-1" color="primary">
                mdi-circle-medium
              </v-icon>
              Hoje
            </span>
          </span>
          <div class="caption">
            {{ dataPrevisaoEntregaGeralLabel }}
          </div>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon @click="onClickExpand">
            <v-icon>
              {{ expandIcon }}
            </v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <transition name="scroll-y-transition">
        <div class="mt-3" v-if="expanded">
          <px-entrega-produtos-compras-item-content
            :saldos="sortedSaldos"
            :compra-produto="compraProduto"/>
        </div>
      </transition>
    </v-card-text>
  </v-card>
</template>

<script>
import { CasaDecimalUtils } from 'px-components';
import { SituacaoEntregaCompra, SituacaoEntregaSaldo } from 'px-business-components';
import { sortBy } from 'lodash';
import moment from 'moment';
import EntregaProdutosComprasItemContent from './EntregaProdutosComprasItemContent.vue';

const situacoesEntregaFinalizadas = [SituacaoEntregaSaldo.keys.ENTREGUE.key,
  SituacaoEntregaSaldo.keys.CANCELADO.key];

const getProximoSaldoPendente = (saldos) => {
  if (!saldos || saldos.length === 0) return null;
  const saldosPendentes = saldos
    .filter(saldo => !situacoesEntregaFinalizadas.includes(saldo.situacaoEntrega))
    .sort((a, b) => new Date(a.dataPrevisaoEntrega) - new Date(b.dataPrevisaoEntrega));
  return saldosPendentes.length > 0 ? saldosPendentes[0] : null;
};

const getUltimoSaldoEntregue = (saldos) => {
  if (!saldos || saldos.length === 0) return null;
  const saldosEntregues = saldos
    .filter(saldo => saldo.situacaoEntrega === SituacaoEntregaSaldo.keys.ENTREGUE.key)
    .sort((a, b) => new Date(b.dataRegistro) - new Date(a.dataRegistro));
  return saldosEntregues.length > 0 ? saldosEntregues[0] : null;
};

export default {
  props: {
    compraProduto: Object,
    loading: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: false,
      loadingOperation: false,
      headers: [
        {
          text: 'Produto',
          sortable: false,
          value: 'descricao',
        },
        {
          text: 'Quantidade',
          value: 'quantidade',
          sortable: false,
        },
        {
          text: '',
          value: 'options',
          align: 'end',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ],
    };
  },
  computed: {
    getCurrencyFormat() {
      return item => CasaDecimalUtils.getCurrencyFormat(item);
    },
    expandIcon() {
      return this.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down';
    },
    isEntregue() {
      return this.compraProduto.situacaoEntrega === SituacaoEntregaCompra.keys.ENTREGUE.key;
    },
    dataPrevisaoEntregaGeralLabel() {
      return this.isEntregue ? 'Última entrega' : 'Previsão de entrega';
    },
    dataPrevisaoEntregaGeral() {
      if (this.isEntregue) {
        const ultimoSaldoEntregue = getUltimoSaldoEntregue(this.compraProduto.saldosEntregas);
        return ultimoSaldoEntregue ? ultimoSaldoEntregue.dataEntrega
          : this.compraProduto.dataPrevisaoEntrega;
      }
      const proximoSaldoPendente = getProximoSaldoPendente(this.compraProduto.saldosEntregas);
      return proximoSaldoPendente ? proximoSaldoPendente.dataPrevisaoEntrega
        : this.compraProduto.dataPrevisaoEntrega;
    },
    sortedSaldos() {
      return sortBy(this.compraProduto.saldosEntregas, ['dataPrevisaoEntrega', 'id']);
    },
    isAtrasado() {
      if (this.isEntregue) {
        return false;
      }
      const dataPrevisaoEntrega = moment(this.dataPrevisaoEntregaGeral);
      const dataAtual = moment();
      return dataPrevisaoEntrega.isBefore(dataAtual, 'day');
    },
    isHoje() {
      if (this.isEntregue) {
        return false;
      }
      const dataPrevisaoEntrega = moment(this.dataPrevisaoEntregaGeral);
      const dataAtual = moment();
      return dataPrevisaoEntrega.isSame(dataAtual, 'day');
    },
  },
  methods: {
    onClickExpand() {
      this.expanded = !this.expanded;
    },
  },
  components: {
    pxEntregaProdutosComprasItemContent: EntregaProdutosComprasItemContent,
  },
};
</script>
